import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useSubscription } from './useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import { ToastAction } from '@/components/ui/toast';

const DAILY_LIMIT = 3;

// Features that have daily limits for free tier
const LIMITED_FEATURES = ['packing_list', 'visa_check', 'jet_lag'] as const;
type LimitedFeature = typeof LIMITED_FEATURES[number];

interface FeatureUsageData {
  [key: string]: number;
}

export function useFeatureUsage() {
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const { toast } = useToast();
  const [usage, setUsage] = useState<FeatureUsageData>({});
  const [loading, setLoading] = useState(true);

  const fetchUsage = useCallback(async () => {
    if (!user) {
      setUsage({});
      setLoading(false);
      return;
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('feature_usage')
        .select('feature_name, usage_count')
        .eq('user_id', user.id)
        .eq('usage_date', today);

      if (error) throw error;

      const usageMap: FeatureUsageData = {};
      data?.forEach(row => {
        usageMap[row.feature_name] = row.usage_count;
      });
      
      setUsage(usageMap);
    } catch (error) {
      console.error('Error fetching feature usage:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const canUseFeature = useCallback((feature: LimitedFeature): boolean => {
    // Paid users have unlimited access
    if (subscription.subscription_tier !== 'economy') {
      return true;
    }

    const currentUsage = usage[feature] || 0;
    return currentUsage < DAILY_LIMIT;
  }, [subscription.subscription_tier, usage]);

  const getRemainingUses = useCallback((feature: LimitedFeature): number => {
    // Paid users have unlimited
    if (subscription.subscription_tier !== 'economy') {
      return Infinity;
    }

    const currentUsage = usage[feature] || 0;
    return Math.max(0, DAILY_LIMIT - currentUsage);
  }, [subscription.subscription_tier, usage]);

  const recordUsage = useCallback(async (feature: LimitedFeature): Promise<boolean> => {
    if (!user) return false;

    // Paid users don't need to track usage
    if (subscription.subscription_tier !== 'economy') {
      return true;
    }

    // Check if user can still use the feature
    if (!canUseFeature(feature)) {
      toast({
        title: "Daily Limit Reached",
        description: `You've used all 3 daily uses for this feature. Upgrade to Premium for unlimited access!`,
        variant: "destructive"
      });
      // #11 Feature Limit Hit — create in-app notification for Economy users
      try {
        await supabase.from('notifications').insert({
          user_id: user.id,
          notification_type: 'upgrade_cta',
          title: 'Feature Limit Reached 🔒',
          message: `You've used all ${DAILY_LIMIT} free daily uses. Upgrade to Premium Economy for unlimited access to Packing Lists, Visa Checks, Jet Lag Plans, and more!`,
          severity: 'medium',
          data: { type: 'feature_limit_hit_cta', feature, upgrade_target: 'premium_economy' },
        });
      } catch (e) {
        console.error('Error creating feature limit notification:', e);
      }
      return false;
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      const currentUsage = usage[feature] || 0;

      // Upsert the usage record
      const { error } = await supabase
        .from('feature_usage')
        .upsert({
          user_id: user.id,
          feature_name: feature,
          usage_date: today,
          usage_count: currentUsage + 1
        }, {
          onConflict: 'user_id,feature_name,usage_date'
        });

      if (error) throw error;

      // Update local state
      setUsage(prev => ({
        ...prev,
        [feature]: currentUsage + 1
      }));

      const remaining = DAILY_LIMIT - (currentUsage + 1);
      if (remaining <= 1 && remaining >= 0) {
        toast({
          title: remaining === 1 ? "⚡ Only 1 use left today!" : "🚀 You've used all free uses today!",
          description: remaining === 1 
            ? "Unlock unlimited access and never worry about limits again." 
            : "Don't let limits slow you down. Go Premium for unlimited features!",
          action: (
            <ToastAction altText="Upgrade Now" onClick={() => window.location.href = '/pricing'}>
              Upgrade Now
            </ToastAction>
          ),
          duration: 8000,
        });
      }

      return true;
    } catch (error) {
      console.error('Error recording feature usage:', error);
      return true; // Allow usage even if tracking fails
    }
  }, [user, subscription.subscription_tier, usage, canUseFeature, toast]);

  return {
    usage,
    loading,
    canUseFeature,
    getRemainingUses,
    recordUsage,
    refreshUsage: fetchUsage,
    DAILY_LIMIT
  };
}
